import { produce } from 'immer'
import { get, set } from 'lodash'

/**
 * Updates a single item in a nested array within React Query cache data.
 * Uses immer for efficient structural sharing - only clones the changed path.
 *
 * Use for updating a single item (e.g. one of thank you screens) in stored TanStack data.
 *
 * @param data - The cached query data to update
 * @param path - Dot-notation path to the array (e.g., 'data.form.thank_you_screens')
 * @param updatedItem - The new item data from the API response
 * @param idKey - Key to use for matching items (defaults to 'id')
 * @returns Updated cache data with structural sharing (efficient)
 *
 * @example
 * queryClient.setQueryData(
 *   campaignsShowQueryKey(campaignId),
 *   (old) => updateCachedArrayItem(old, 'data.form.thank_you_screens', updatedScreen)
 * )
 */
export function updateCachedArrayItem<T, TItem>(
	data: T | undefined,
	path: string,
	updatedItem: TItem,
	idKey: keyof TItem = 'id' as keyof TItem,
): T | undefined {
	if (!data) { return data }

	return produce(data, (draft) => {
		const array = get(draft, path)

		if (!Array.isArray(array)) {
			return
		}

		const index = array.findIndex((item: TItem) => item[idKey] === updatedItem[idKey])

		if (index !== -1) {
			array[index] = updatedItem
		}
	})
}

/**
 * Replaces an entire array in nested React Query cache data.
 * Uses immer for efficient structural sharing - only clones the changed path.
 *
 * Use for replacing a whole entry (e.g. welcome screens, candidates) in stored TanStack data.
 *
 * @param data - The cached query data to update
 * @param path - Dot-notation path to the array (e.g., 'data.form.thank_you_screens')
 * @param newArray - The new array to replace with
 * @returns Updated cache data with structural sharing (efficient)
 *
 * @example
 * queryClient.setQueryData(
 *   campaignsShowQueryKey(campaignId),
 *   (old) => replaceCachedArray(old, 'data.form.thank_you_screens', newScreens)
 * )
 */
export function replaceCachedArray<T, TItem>(
	data: T | undefined,
	path: string,
	newArray: TItem[],
): T | undefined {
	if (!data) { return data }

	return produce(data, (draft) => {
		set(draft as Object, path, newArray)
	})
}
